/**
 * WebAuthn / Passkey client helpers.
 *
 * Provides:
 *  - base64url <-> ArrayBuffer/Uint8Array conversions
 *  - decoders for server-sent PublicKeyCredential(Creation|Request)OptionsJSON
 *  - encoders for PublicKeyCredential -> server JSON
 *  - webauthnFetchJSON wrapper that handles JSON request/response + error shape
 */

import { fetchWithAuth } from '@/lib/utils/api-auth';

// ---------------------------------------------------------------------------
// base64url helpers
// ---------------------------------------------------------------------------

export function base64urlToBuffer(b64: string): Uint8Array<ArrayBuffer> {
  if (typeof b64 !== 'string') {
    throw new TypeError('base64urlToBuffer expects a string');
  }
  // Replace URL-safe chars and pad to multiple of 4.
  const normalized = b64.replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4;
  const padded = pad === 0 ? normalized : normalized + '='.repeat(4 - pad);
  const binary = atob(padded);
  // Explicitly allocate an ArrayBuffer so the view's buffer is narrowly typed
  // (not `ArrayBufferLike` which TS treats as incompatible with BufferSource).
  const buffer = new ArrayBuffer(binary.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

export function bufferToBase64url(buf: ArrayBuffer | Uint8Array | null | undefined): string {
  if (!buf) return '';
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = '';
  // Process in chunks to avoid call-stack issues with String.fromCharCode on large buffers.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    binary += String.fromCharCode.apply(null, Array.from(slice));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ---------------------------------------------------------------------------
// Decoders: server JSON -> browser-native PublicKeyCredential options
// ---------------------------------------------------------------------------

type JsonPkcDescriptor = {
  type?: string;
  id: string;
  transports?: AuthenticatorTransport[];
};

interface JsonCreationOptions {
  challenge: string;
  rp: PublicKeyCredentialRpEntity;
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  excludeCredentials?: JsonPkcDescriptor[];
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
  extensions?: AuthenticationExtensionsClientInputs;
}

interface JsonRequestOptions {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: JsonPkcDescriptor[];
  userVerification?: UserVerificationRequirement;
  extensions?: AuthenticationExtensionsClientInputs;
  mediation?: CredentialMediationRequirement;
}

function decodeDescriptor(d: JsonPkcDescriptor): PublicKeyCredentialDescriptor {
  return {
    type: 'public-key',
    id: base64urlToBuffer(d.id),
    transports: d.transports,
  };
}

/**
 * Decodes the server's `PublicKeyCredentialCreationOptionsJSON` shape
 * into a browser-native `PublicKeyCredentialCreationOptions` whose
 * binary fields are `ArrayBuffer`s.
 */
export function decodeCreationOptions(opts: JsonCreationOptions): PublicKeyCredentialCreationOptions {
  return {
    challenge: base64urlToBuffer(opts.challenge),
    rp: opts.rp,
    user: {
      id: base64urlToBuffer(opts.user.id),
      name: opts.user.name,
      displayName: opts.user.displayName,
    },
    pubKeyCredParams: opts.pubKeyCredParams,
    timeout: opts.timeout,
    excludeCredentials: opts.excludeCredentials?.map(decodeDescriptor),
    authenticatorSelection: opts.authenticatorSelection,
    attestation: opts.attestation,
    extensions: opts.extensions,
  };
}

/**
 * Decodes the server's `PublicKeyCredentialRequestOptionsJSON` shape
 * into a browser-native `PublicKeyCredentialRequestOptions`.
 */
export function decodeRequestOptions(opts: JsonRequestOptions): PublicKeyCredentialRequestOptions {
  return {
    challenge: base64urlToBuffer(opts.challenge),
    timeout: opts.timeout,
    rpId: opts.rpId,
    allowCredentials: opts.allowCredentials?.map(decodeDescriptor),
    userVerification: opts.userVerification,
    extensions: opts.extensions,
  };
}

// ---------------------------------------------------------------------------
// Encoders: PublicKeyCredential -> server JSON
// ---------------------------------------------------------------------------

type AttestationResponseJSON = {
  id: string;
  rawId: string;
  type: 'public-key';
  authenticatorAttachment?: AuthenticatorAttachment | null;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: string[];
    publicKeyAlgorithm?: number;
    publicKey?: string;
    authenticatorData?: string;
  };
};

type AssertionResponseJSON = {
  id: string;
  rawId: string;
  type: 'public-key';
  authenticatorAttachment?: AuthenticatorAttachment | null;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
};

/**
 * Encodes a `PublicKeyCredential` produced by `navigator.credentials.create()`
 * into a server-friendly JSON structure (PublicKeyCredentialJSON-ish).
 */
export function encodeAttestationResponse(cred: PublicKeyCredential): AttestationResponseJSON {
  const resp = cred.response as AuthenticatorAttestationResponse;

  // These methods are available on modern browsers only.
  type MaybeExtendedAttestationResponse = AuthenticatorAttestationResponse & {
    getTransports?: () => string[];
    getAuthenticatorData?: () => ArrayBuffer;
    getPublicKey?: () => ArrayBuffer | null;
    getPublicKeyAlgorithm?: () => number;
  };
  const extended = resp as MaybeExtendedAttestationResponse;

  const transports = typeof extended.getTransports === 'function' ? extended.getTransports() : undefined;
  const authenticatorData =
    typeof extended.getAuthenticatorData === 'function' ? extended.getAuthenticatorData() : undefined;
  const publicKey = typeof extended.getPublicKey === 'function' ? extended.getPublicKey() : undefined;
  const publicKeyAlgorithm =
    typeof extended.getPublicKeyAlgorithm === 'function' ? extended.getPublicKeyAlgorithm() : undefined;

  const authenticatorAttachment =
    (cred as PublicKeyCredential & { authenticatorAttachment?: AuthenticatorAttachment | null })
      .authenticatorAttachment ?? null;

  return {
    id: cred.id,
    rawId: bufferToBase64url(cred.rawId),
    type: 'public-key',
    authenticatorAttachment,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(resp.clientDataJSON),
      attestationObject: bufferToBase64url(resp.attestationObject),
      transports,
      publicKeyAlgorithm,
      publicKey: publicKey ? bufferToBase64url(publicKey) : undefined,
      authenticatorData: authenticatorData ? bufferToBase64url(authenticatorData) : undefined,
    },
  };
}

/**
 * Encodes a `PublicKeyCredential` produced by `navigator.credentials.get()`
 * into a server-friendly JSON structure.
 */
export function encodeAssertionResponse(cred: PublicKeyCredential): AssertionResponseJSON {
  const resp = cred.response as AuthenticatorAssertionResponse;
  const authenticatorAttachment =
    (cred as PublicKeyCredential & { authenticatorAttachment?: AuthenticatorAttachment | null })
      .authenticatorAttachment ?? null;

  return {
    id: cred.id,
    rawId: bufferToBase64url(cred.rawId),
    type: 'public-key',
    authenticatorAttachment,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(resp.clientDataJSON),
      authenticatorData: bufferToBase64url(resp.authenticatorData),
      signature: bufferToBase64url(resp.signature),
      userHandle: resp.userHandle ? bufferToBase64url(resp.userHandle) : null,
    },
  };
}

// ---------------------------------------------------------------------------
// webauthnFetchJSON
// ---------------------------------------------------------------------------

/**
 * Options for {@link webauthnFetchJSON}.
 */
export type WebauthnFetchOptions = {
  /**
   * HTTP method. Defaults to 'POST' when `body` is provided, 'GET' otherwise.
   */
  method?: string;
  /**
   * When true, the request is issued with `fetchWithAuth` (credentials-include, x-zenopus-client, 401 event).
   * When false, a plain `fetch` is used (for unauthenticated endpoints like login-begin/finish,
   * recovery request/consume).
   *
   * Default: true.
   */
  authenticated?: boolean;
  /**
   * Extra headers merged over `Content-Type: application/json`.
   */
  headers?: Record<string, string>;
  /**
   * AbortSignal forwarded to fetch.
   */
  signal?: AbortSignal;
  /**
   * Hard cap for how long the request may be in flight, in milliseconds. When
   * the deadline elapses the request is aborted with a timeout error. The
   * caller-supplied `signal` (if any) is honoured in parallel — whichever
   * fires first wins. Default: 30_000 (30 s).
   */
  timeoutMs?: number;
};

export class WebauthnFetchError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'WebauthnFetchError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Maps a server-side passkey error code to a stable i18n key. Callers should
 * fall back to the `message` surfaced by {@link WebauthnFetchError} (or a
 * generic key) when `i18nKeyForPasskeyError` returns the generic fallback.
 */
export function i18nKeyForPasskeyError(code: string | undefined): string {
  const map: Record<string, string> = {
    passkey_sso_only: 'auth.passkey.ssoOnlyForAdmins',
    passkey_forbidden: 'auth.passkey.forbiddenForAccount',
    passkey_too_many: 'auth.passkey.tooManyCredentials',
    passkey_recovery_token_consumed_or_expired: 'auth.passkey.recoveryLinkInvalid',
    invalid_token: 'auth.passkey.recoveryLinkInvalid',
    invalid_code: 'auth.passkey.recoveryCodeInvalid',
    invalid_body: 'auth.passkey.genericError',
    invalid_user: 'auth.passkey.genericError',
    invalid_registration: 'auth.passkey.registrationFailed',
    challenge_expired: 'auth.passkey.challengeExpired',
    challenge_mismatch: 'auth.passkey.challengeExpired',
    invalid_assertion: 'auth.passkey.loginFailed',
    credential_already_registered: 'auth.passkey.alreadyRegistered',
    passkey_hardware_required: 'auth.passkey.hardwareRequired',
    passkey_last_credential_protected: 'auth.passkey.lastCredentialProtected',
    passkey_required: 'auth.passkey.adminRequiresPasskey',
    passkey_reenroll_required: 'auth.passkey.reEnrollRequired',
    passkey_clone_detected: 'auth.passkey.cloneDetected',
    uv_reauth_required: 'auth.passkey.uvReauthRequired',
    rate_limited: 'auth.passkey.rateLimited',
    auth_required: 'auth.passkey.authRequired',
    admin_required: 'auth.passkey.adminOnly',
    org_membership_required: 'auth.passkey.orgMembershipRequired',
    // Transport-layer failures: webauthnFetchJSON throws `network_error` when
    // the fetch itself fails (timeout, CORS, gateway 5xx with non-JSON body,
    // etc.). The technical detail (`network_error status=N`) lives in the
    // error's `message` for log triage; the UI must translate via this key.
    network_error: 'auth.passkey.networkError',
    timeout: 'auth.passkey.networkError',
  };
  if (!code) return 'auth.passkey.genericError';
  return map[code] ?? 'auth.passkey.genericError';
}

/**
 * POSTs a JSON body to `url` and parses a JSON response. On non-2xx responses
 * a {@link WebauthnFetchError} is thrown with the server's `code`/`message`
 * fields when available.
 *
 * @param url  Relative API path (e.g. `/api/auth/passkey/register/begin`).
 * @param body Request body; serialized with JSON.stringify. Pass `undefined` to skip a body.
 * @param opts See {@link WebauthnFetchOptions}.
 */
export async function webauthnFetchJSON<T = unknown>(
  url: string,
  body?: unknown,
  opts: WebauthnFetchOptions = {},
): Promise<T> {
  const { method, authenticated = true, headers = {}, signal, timeoutMs = 30_000 } = opts;
  const resolvedMethod = method ?? (body === undefined ? 'GET' : 'POST');

  // Compose the caller's signal (if any) with a local timeout controller so
  // neither source is lost. If AbortSignal.any is available (all current
  // evergreen targets) we use it directly; otherwise we fall back to a
  // manually-wired controller so jsdom/older Safari still behave.
  const timeoutCtl = new AbortController();
  const timer = setTimeout(() => timeoutCtl.abort(new Error('webauthnFetchJSON timeout')), timeoutMs);
  let combinedSignal: AbortSignal = timeoutCtl.signal;
  if (signal) {
    const anyCtor = (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any;
    if (typeof anyCtor === 'function') {
      combinedSignal = anyCtor.call(AbortSignal, [signal, timeoutCtl.signal]);
    } else {
      signal.addEventListener('abort', () => timeoutCtl.abort(signal.reason), { once: true });
    }
  }

  const init: RequestInit = {
    method: resolvedMethod,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
    signal: combinedSignal,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const doFetch = authenticated
    ? (input: RequestInfo, i: RequestInit) => fetchWithAuth(input, i)
    : (input: RequestInfo, i: RequestInit) => fetch(input, { ...i, credentials: 'include' });

  let response: Response;
  try {
    response = await doFetch(url, init);
  } catch (error) {
    // Distinguish deadline-fired aborts from caller-supplied aborts: the
    // timer fires only when timeoutCtl itself aborted; a caller-cancel
    // surfaces as whichever abort signal they passed.
    if (timeoutCtl.signal.aborted && !signal?.aborted) {
      throw new WebauthnFetchError(`Request timed out after ${timeoutMs}ms`, 0, 'timeout');
    }
    const message = error instanceof Error ? error.message : String(error);
    throw new WebauthnFetchError(`Network error: ${message}`, 0, 'network_error');
  } finally {
    clearTimeout(timer);
  }

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  let parsed: unknown = null;
  if (isJson) {
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
  } else {
    try {
      const text = await response.text();
      parsed = text ? { message: text } : null;
    } catch {
      parsed = null;
    }
  }

  const isEnvelope = parsed && typeof parsed === 'object' && 'success' in (parsed as Record<string, unknown>);

  if (!response.ok) {
    // Guard against non-JSON or malformed responses. We avoid hardcoding any
    // English fallback message here — callers must translate `network_error`
    // via i18nKeyForPasskeyError so the displayed message matches the user's
    // locale. The WebauthnFetchError.message remains an English technical
    // detail (status code) for log triage; UI must NOT render it directly.
    if (!isJson || !parsed || typeof parsed !== 'object') {
      throw new WebauthnFetchError(`network_error status=${response.status}`, response.status, 'network_error', parsed);
    }
    const obj = parsed as Record<string, unknown>;
    // Envelope: { success:false, error:<code>, message:<human> }
    // Also accept legacy `{ code, message }` shape for robustness.
    const code =
      (typeof obj.error === 'string' ? obj.error : undefined) ??
      (typeof obj.code === 'string' ? obj.code : undefined) ??
      'unknown';
    const message =
      (typeof obj.message === 'string' && obj.message) ||
      (typeof obj.error === 'string' && obj.error) ||
      response.statusText ||
      '';
    throw new WebauthnFetchError(message, response.status, code, parsed);
  }

  // 2xx: unwrap `{ success: true, data: <T> }`. Fall back to the raw body for
  // legacy endpoints that have not yet been migrated to the envelope.
  if (isEnvelope) {
    const obj = parsed as Record<string, unknown>;
    if (obj.success === true && 'data' in obj) {
      return obj.data as T;
    }
  }
  return parsed as T;
}
